import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowRight } from 'lucide-react';
import { sanitizeHtml } from '@/utils/sanitize';
import { mediaUrl } from '@/lib/mediaUrl';
import { API } from '@/config';

const Cta = ({ label, url, primary, testId }) => {
  if (!label || !url) return null;
  const cls = primary
    ? 'h-12 px-6 rounded-full bg-[#20acf6] hover:bg-[#3bb8f7] text-white text-base shadow-[0_10px_24px_-12px_rgba(32,172,246,0.8)]'
    : 'h-12 px-6 rounded-full bg-white border border-[#2a3e5d]/20 text-[#2a3e5d] hover:bg-[#2a3e5d] hover:text-white text-base';
  const inner = <>{label}{primary && <ArrowRight className="h-4 w-4 ml-2" />}</>;
  return (
    <Button asChild className={`${cls} transition-colors`}>
      {/^https?:\/\//.test(url) ? <a href={url} target="_blank" rel="noopener noreferrer" data-testid={testId}>{inner}</a> : <Link to={url} data-testid={testId}>{inner}</Link>}
    </Button>
  );
};

const Hero = ({ page }) => (
  <section className="relative overflow-hidden bg-[#F1F6FB]">
    <div className="absolute -left-24 -top-24 w-80 h-80 rounded-full bg-[#02d1d4]/10" />
    <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid gap-10 items-center ${page.featured_image_url ? 'lg:grid-cols-[1.05fr_1fr]' : ''}`}>
      <div className="max-w-2xl">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2a3e5d] leading-[1.06] tracking-tight" data-testid="page-title">{page.title}</h1>
        {page.excerpt && <p className="mt-5 text-base md:text-lg text-[#1e293b]/70 leading-relaxed" data-testid="page-excerpt">{page.excerpt}</p>}
        {(page.cta_primary_label || page.cta_secondary_label) && (
          <div className="mt-8 flex flex-wrap gap-3">
            <Cta label={page.cta_primary_label} url={page.cta_primary_url} primary testId="page-cta-primary" />
            <Cta label={page.cta_secondary_label} url={page.cta_secondary_url} testId="page-cta-secondary" />
          </div>
        )}
      </div>
      {page.featured_image_url && (
        <div className="relative">
          <div className="absolute -right-4 -bottom-4 w-full h-full rounded-[2rem] bg-[#20acf6]/15" />
          <img src={mediaUrl(page.featured_image_url)} alt={page.featured_image_alt || ''} className="relative w-full h-[320px] lg:h-[420px] object-cover rounded-[2rem]" data-testid="page-hero-image" />
        </div>
      )}
    </div>
  </section>
);

const Shell = ({ children, testId }) => (
  <div className="min-h-screen flex flex-col bg-[#F8FAFC]" data-testid={testId}>
    <Header />
    {children}
    <Footer />
  </div>
);

const CustomPage = ({ slug: fixedSlug }) => {
  const params = useParams();
  const slug = fixedSlug || params.slug;
  const [page, setPage] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    setState('loading');
    axios.get(`${API}/cms/pages/by-slug/${slug}`).then((res) => {
      setPage(res.data);
      setState('ok');
      document.title = res.data.seo_title || `${res.data.nav_label || res.data.title} | Common Impact E-learning`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta && res.data.seo_description) meta.setAttribute('content', res.data.seo_description);
    }).catch(() => setState('missing'));
  }, [slug]);

  if (state === 'loading') {
    return <Shell><div className="flex-1 flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div></Shell>;
  }
  if (state === 'missing') {
    return (
      <Shell>
        <div className="flex-1 flex items-center justify-center py-32 text-center px-4">
          <div>
            <h1 className="text-4xl font-bold text-[#2a3e5d] mb-3">Page not found</h1>
            <p className="text-[#1e293b]/60">The page you're looking for doesn't exist or has been moved.</p>
            <Button asChild className="mt-6 bg-[#20acf6] hover:bg-[#3bb8f7]"><Link to="/">Back to home</Link></Button>
          </div>
        </div>
      </Shell>
    );
  }
  const hasCta = page.cta_primary_label || page.cta_secondary_label;
  return (
    <Shell testId="custom-page">
      <main className="flex-1">
        <Hero page={page} />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <article className="cms-content prose prose-lg max-w-none text-[#1e293b]" dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.content) }} data-testid="page-content" />
        </div>
        {hasCta && (
          <section className="bg-[#2a3e5d]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <p className="text-2xl md:text-3xl font-bold text-white max-w-xl">{page.excerpt || page.title}</p>
              <div className="flex flex-wrap gap-3">
                <Cta label={page.cta_primary_label} url={page.cta_primary_url} primary testId="page-cta-bottom-primary" />
                <Cta label={page.cta_secondary_label} url={page.cta_secondary_url} testId="page-cta-bottom-secondary" />
              </div>
            </div>
          </section>
        )}
      </main>
    </Shell>
  );
};

export default CustomPage;
